<template>
  <div class="refunds-page py-3">
    <header class="rf-toolbar">
      <div class="rf-toolbar__title">
        <span class="rf-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Refunds</h1>
          <p>Review the refund queue</p>
        </div>
      </div>
      <div class="rf-toolbar__actions">
        <button
          type="button"
          class="rf-refresh-btn"
          :class="{ 'is-loading': isLoadingList }"
          :disabled="isLoadingList"
          @click="loadRefunds(currentPage)"
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="rf-refresh-btn__icon" aria-hidden="true">
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
          <span>{{ isLoadingList ? 'Refreshing…' : 'Refresh' }}</span>
        </button>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="rf-alert">{{ errorMessage }}</CAlert>

    <BaseTable
      title="Refund Queue"
      :items="tableRows"
      :columns="columns"
      :loading="isLoadingList"
      :sortable="false"
      :show-count="false"
      :empty-text="emptyText"
    >
      <template #toolbar>
        <div class="rf-table-tools">
          <!-- No Reset button: "All Statuses" is the first option, so the
               dropdown clears itself and a second control would just cost
               the header width. -->
          <AppDropdown
            class="rf-status-filter"
            :value="filters.status"
            :options="statusOptions"
            placeholder="All Statuses"
            @input="onStatusChange"
          />
          <span v-if="refunds.length" class="rf-totals__chip">Showing {{ pageRangeLabel }}</span>
        </div>
      </template>

      <template #cell-amount="{ row }">
        <span class="rf-amount">₹{{ row.amount_display }}</span>
      </template>

      <template #cell-status="{ row }">
        <span :class="['badge', row.status_class]">{{ row.status_label }}</span>
      </template>

      <template #cell-name="{ row }">
        <div class="rf-name">{{ row.name || '—' }}</div>
        <div class="rf-name__sub">{{ row.bank_account_no || '—' }}</div>
      </template>

      <template #cell-reason="{ value }">
        <span class="rf-reason" :title="value || ''">{{ value || '—' }}</span>
      </template>

      <template #cell-action="{ row }">
        <button type="button" class="rf-view-btn" @click="openRefund(row.raw)">
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
          <span>View</span>
        </button>
      </template>
    </BaseTable>

    <div v-if="!isLoadingList && refunds.length && lastPage > 1" class="rf-pagination-bar">
      <span class="rf-pagination-info">{{ pageRangeLabel }}</span>
      <AppPager :current-page="currentPage" :last-page="lastPage" @change="loadRefunds" />
    </div>

    <CModal
      class="view-order-modal"
      :show.sync="modal.show"
      size="lg"
      :centered="true"
      title="Refund Details"
    >
      <div v-if="modal.refund" class="vo-body">
        <div class="vo-section">
          <div class="vo-row">
            <span class="vo-key">ID</span>
            <span class="vo-val">#{{ modal.refund.id }}</span>
          </div>
          <div class="vo-row">
            <span class="vo-key">Order Number</span>
            <span class="vo-val">{{ refundOrderNumber(modal.refund) }}</span>
          </div>
          <div class="vo-row">
            <span class="vo-key">Customer Name</span>
            <span class="vo-val">{{ modal.refund.name || '—' }}</span>
          </div>
          <div class="vo-row">
            <span class="vo-key">Amount</span>
            <span class="vo-val rf-modal-amount">₹{{ formatAmount(modal.refund.amount) }}</span>
          </div>
          <div class="vo-row">
            <span class="vo-key">Status</span>
            <span class="vo-val"><span :class="['badge', statusClass(modal.refund.status)]">{{ statusLabel(modal.refund.status) }}</span></span>
          </div>
        </div>

        <div class="vo-section">
          <div class="vo-row">
            <span class="vo-key">Bank Account Number</span>
            <span class="vo-val">{{ modal.refund.bank_account_no || '—' }}</span>
          </div>
          <div class="vo-row">
            <span class="vo-key">IFSC Code</span>
            <span class="vo-val">{{ modal.refund.IFSC || '—' }}</span>
          </div>
          <div class="vo-row">
            <span class="vo-key">Reason</span>
            <span class="vo-val">{{ modal.refund.reason || '—' }}</span>
          </div>
          <div class="vo-row">
            <span class="vo-key">Requested On</span>
            <span class="vo-val">{{ formatTimestamp(modal.refund.created_at) }}</span>
          </div>
          <div class="vo-row">
            <span class="vo-key">Actioned On</span>
            <span class="vo-val">
              <template v-if="isActioned(modal.refund)">{{ actionedDisplay(modal.refund) }}</template>
              <span v-else class="rf-not-actioned">Awaiting action</span>
            </span>
          </div>
        </div>

        <div v-if="proofUrl(modal.refund)" class="vo-section vo-section--proof">
          <div class="rf-proof-head">
            <span class="rf-proof-title">Payment Proof</span>
            <a class="rf-proof-open" :href="proofUrl(modal.refund)" target="_blank" rel="noopener">
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
              <span>Open full size</span>
            </a>
          </div>
          <div class="rf-proof-preview">
            <img
              v-if="isProofImage(modal.refund)"
              :src="proofUrl(modal.refund)"
              :alt="`Refund proof #${modal.refund.id}`"
              class="rf-proof-image"
              loading="lazy"
            />
            <iframe
              v-else-if="isProofPdf(modal.refund)"
              :src="proofUrl(modal.refund)"
              class="rf-proof-pdf"
              title="Refund proof PDF"
              loading="lazy"
              referrerpolicy="no-referrer"
            ></iframe>
            <div v-else class="rf-proof-fallback">
              <span>Proof attached — preview not available for this file type.</span>
              <a :href="proofUrl(modal.refund)" target="_blank" rel="noopener">Open in a new tab</a>
            </div>
          </div>
        </div>
      </div>
      <template #footer>
        <CButton
          v-if="canActOnRefund(modal.refund)"
          color="danger"
          :disabled="actionBusy"
          @click="askCancelRefund"
        >
          {{ actionBusy && pendingAction === 'cancel' ? 'Cancelling…' : 'Cancel Request' }}
        </CButton>
        <CButton
          v-if="canActOnRefund(modal.refund)"
          color="success"
          :disabled="actionBusy"
          @click="approveRefund"
        >
          {{ actionBusy && pendingAction === 'approve' ? 'Approving…' : 'Approve' }}
        </CButton>
        <CButton color="secondary" variant="outline" :disabled="actionBusy" @click="modal.show = false">Close</CButton>
      </template>
    </CModal>

    <AppConfirmDialog
      :visible.sync="confirmDialog.visible"
      title="Cancel refund request?"
      :message="confirmDialog.message"
      confirm-label="Cancel Request"
      cancel-label="Keep"
      busy-label="Cancelling…"
      :busy="actionBusy && pendingAction === 'cancel'"
      variant="danger"
      @confirm="cancelRefund"
    />
  </div>
</template>

<script>
import axios from 'axios'
import AppConfirmDialog from '@/components/AppConfirmDialog.vue'
import AppDropdown from '@/components/AppDropdown.vue'
import AppPager from '@/components/AppPager.vue'
import BaseTable from '@/components/BaseTable.vue'

// Mirrors RefundController::STATUSES — the only values the API accepts
// for the ?status filter. An empty value means "no filter".
const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'approval_pending', label: 'Approval Pending' },
  { value: 'completed', label: 'Completed' },
  { value: 'rejected', label: 'Rejected' }
]

// Statuses that mean accounts has acted on the request. Only these carry a
// meaningful action date — see actionedDisplay().
const ACTIONED_STATUSES = ['completed', 'rejected']

export default {
  name: 'Refunds',
  components: { AppConfirmDialog, AppDropdown, AppPager, BaseTable },
  data () {
    return {
      refunds: [],
      filters: { status: '' },
      statusOptions: STATUS_OPTIONS,
      currentPage: 1,
      lastPage: 1,
      perPage: 10,
      total: 0,
      isLoadingList: false,
      errorMessage: '',
      modal: { show: false, refund: null },
      actionBusy: false,
      pendingAction: '',
      confirmDialog: { visible: false, message: '' },
      columns: [
        { key: 'id', label: 'ID', width: '70px' },
        { key: 'order_number', label: 'Order' },
        { key: 'name', label: 'Customer' },
        { key: 'amount', label: 'Amount', align: 'right' },
        { key: 'status', label: 'Status' },
        { key: 'reason', label: 'Reason', width: '260px' },
        { key: 'requested', label: 'Requested' },
        { key: 'actioned', label: 'Actioned' },
        { key: 'action', label: 'Action', align: 'right', width: '110px' }
      ]
    }
  },
  computed: {
    tableRows () {
      return this.refunds.map((refund) => ({
        id: refund.id,
        order_number: this.refundOrderNumber(refund),
        name: refund.name,
        bank_account_no: refund.bank_account_no,
        amount_display: this.formatAmount(refund.amount),
        amount: Number(refund.amount) || 0,
        status: refund.status,
        status_label: this.statusLabel(refund.status),
        status_class: this.statusClass(refund.status),
        reason: refund.reason,
        requested: this.formatTimestamp(refund.created_at),
        actioned: this.actionedDisplay(refund),
        raw: refund
      }))
    },
    emptyText () {
      const active = STATUS_OPTIONS.find((o) => o.value === this.filters.status)
      return active && active.value
        ? `No ${active.label.toLowerCase()} refunds found.`
        : 'No refunds found.'
    },
    pageRangeLabel () {
      if (!this.refunds.length) return '0'
      const start = (this.currentPage - 1) * this.perPage + 1
      const end = start + this.refunds.length - 1
      const total = this.total || end
      return `${start}–${end} of ${total}`
    }
  },
  created () {
    this.loadRefunds(1)
  },
  methods: {
    authToken () {
      return localStorage.getItem('api_token')
    },
    parseError (error, fallback) {
      const validationErrors = error && error.response && error.response.data && error.response.data.error
      if (validationErrors && typeof validationErrors === 'object') {
        const key = Object.keys(validationErrors)[0]
        if (key && Array.isArray(validationErrors[key]) && validationErrors[key][0]) {
          return validationErrors[key][0]
        }
      }
      return (error && error.response && error.response.data && error.response.data.message) || fallback
    },
    formatDateTime (value) {
      if (!value) return '—'
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return '—'
      return d.toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      })
    },
    // created_at / updated_at are cast 'timestamp' on the Refund model, so
    // they arrive as Unix SECONDS (a bare 10-digit int), not ISO strings.
    // Passing seconds straight to new Date() reads them as milliseconds and
    // renders 1970 — the trap documented in client/CLAUDE.md. Shape-detects
    // rather than assuming, so an ISO string still formats correctly if the
    // cast is ever changed server-side.
    formatTimestamp (value) {
      if (value === null || value === undefined || value === '') return '—'
      const isUnixSeconds = typeof value === 'number' || /^\d+$/.test(String(value).trim())
      if (isUnixSeconds) {
        const seconds = Number(value)
        if (!Number.isFinite(seconds) || seconds <= 0) return '—'
        return this.formatDateTime(seconds * 1000)
      }
      return this.formatDateTime(value)
    },
    formatAmount (value) {
      const n = Number(value)
      if (!Number.isFinite(n)) return '0.00'
      return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    isActioned (refund) {
      return !!(refund && ACTIONED_STATUSES.includes(refund.status))
    },
    // When the accounts team processed the request. There is no dedicated
    // column for this: the refunds table is only ever written on creation
    // and on a status transition (approve / cancel / mark-paid / inbox
    // resolve — there is no edit path for a refund), so updated_at IS the
    // moment it was actioned.
    //
    // Gate on status, NOT on comparing the two dates: a pending refund is
    // never re-saved, so its updated_at simply mirrors created_at and would
    // otherwise render the request date in the Actioned column.
    actionedDisplay (refund) {
      if (!this.isActioned(refund)) return '—'
      return this.formatTimestamp(refund.updated_at)
    },
    refundOrderNumber (refund) {
      if (refund && refund.order && refund.order.order_number) return refund.order.order_number
      if (refund && refund.order_number) return refund.order_number
      return refund && refund.order_id ? `#${refund.order_id}` : '—'
    },
    statusLabel (status) {
      if (!status) return 'N/A'
      return String(status).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    },
    statusClass (status) {
      if (status === 'completed') return 'badge--green'
      if (status === 'approval_pending') return 'badge--yellow'
      if (status === 'rejected') return 'badge--red'
      return 'badge--gray'
    },
    proofUrl (refund) {
      if (!refund) return ''
      const fromImageField = refund.imageFile
      if (fromImageField && /^https?:\/\//i.test(fromImageField)) return fromImageField
      const proof = refund.payment_proof || fromImageField
      if (!proof) return ''
      if (/^https?:\/\//i.test(proof)) return proof
      return `${this.$apiAdress}/${String(proof).replace(/^\/+/, '')}`
    },
    proofExtension (refund) {
      const url = this.proofUrl(refund)
      if (!url) return ''
      const stripped = String(url).split('?')[0].split('#')[0]
      const dot = stripped.lastIndexOf('.')
      if (dot === -1) return ''
      return stripped.slice(dot + 1).toLowerCase()
    },
    isProofImage (refund) {
      return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(this.proofExtension(refund))
    },
    isProofPdf (refund) {
      return this.proofExtension(refund) === 'pdf'
    },
    openRefund (refund) {
      this.modal.refund = refund
      this.modal.show = true
    },
    canActOnRefund (refund) {
      return !!(refund && refund.status === 'approval_pending')
    },
    askCancelRefund () {
      const r = this.modal.refund
      if (!r) return
      this.confirmDialog.message = `Cancel refund request #${r.id} for ${r.name || 'this customer'}? This sets the status to rejected and can't be undone here.`
      this.confirmDialog.visible = true
    },
    approveRefund () {
      const r = this.modal.refund
      if (!r || this.actionBusy) return
      this.actionBusy = true
      this.pendingAction = 'approve'
      this.errorMessage = ''
      axios.post(`${this.$apiAdress}/api/refunds/approve?token=${this.authToken()}`, { id: r.id })
        .then((response) => {
          const updated = response && response.data && response.data.refund
          if (updated) {
            this.modal.refund = { ...r, ...updated }
            this.patchRefundInList(updated)
          }
          this.modal.show = false
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Unable to approve refund.')
        })
        .finally(() => {
          this.actionBusy = false
          this.pendingAction = ''
        })
    },
    cancelRefund () {
      const r = this.modal.refund
      if (!r || this.actionBusy) return
      this.actionBusy = true
      this.pendingAction = 'cancel'
      this.errorMessage = ''
      axios.post(`${this.$apiAdress}/api/refunds/cancel?token=${this.authToken()}`, { id: r.id })
        .then((response) => {
          const updated = response && response.data && response.data.refund
          if (updated) {
            this.modal.refund = { ...r, ...updated }
            this.patchRefundInList(updated)
          }
          this.confirmDialog.visible = false
          this.modal.show = false
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Unable to cancel refund.')
          this.confirmDialog.visible = false
        })
        .finally(() => {
          this.actionBusy = false
          this.pendingAction = ''
        })
    },
    patchRefundInList (updated) {
      // With a status filter on, approving/rejecting moves the row out of
      // the current result set — patching in place would leave a
      // "Completed" row sitting under a "Approval Pending" filter, and the
      // server-side total would be stale too. Refetch instead.
      if (this.filters.status) {
        this.loadRefunds(this.currentPage)
        return
      }
      const idx = this.refunds.findIndex((x) => x && x.id === updated.id)
      if (idx >= 0) {
        this.$set(this.refunds, idx, { ...this.refunds[idx], ...updated })
      }
    },
    onStatusChange (value) {
      if (value === this.filters.status) return
      this.filters.status = value
      // Page 1 — the old page number is meaningless against a different
      // result set (page 4 of "all" may not exist once filtered).
      this.loadRefunds(1)
    },
    loadRefunds (page = 1) {
      this.isLoadingList = true
      this.errorMessage = ''

      const statusParam = this.filters.status
        ? `&status=${encodeURIComponent(this.filters.status)}`
        : ''

      axios.get(`${this.$apiAdress}/api/refund?page=${page}${statusParam}&token=${this.authToken()}`)
        .then((response) => {
          const data = response.data || {}
          this.refunds = Array.isArray(data.data) ? data.data : []
          this.currentPage = Number(data.current_page || 1)
          this.lastPage = Number(data.last_page || 1)
          this.perPage = Number(data.per_page || this.perPage)
          this.total = Number(data.total || 0)
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Unable to load refunds.')
        })
        .finally(() => {
          this.isLoadingList = false
        })
    }
  }
}
</script>

<style scoped>
.refunds-page {
  padding: 0.25rem 0;
}

.rf-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
}

.rf-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.rf-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.rf-toolbar__title p {
  margin: 0.1rem 0 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.rf-toolbar__accent {
  width: 4px;
  height: 32px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
}

.rf-toolbar__actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.rf-refresh-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 34px;
  padding: 0 0.85rem;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 600;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}

.rf-refresh-btn:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.12);
}

.rf-refresh-btn__icon {
  color: #16a07a;
  transition: transform 0.4s ease;
  flex-shrink: 0;
}

.rf-refresh-btn:hover:not(:disabled) .rf-refresh-btn__icon {
  transform: rotate(60deg);
}

.rf-refresh-btn.is-loading .rf-refresh-btn__icon {
  animation: rf-spin 0.9s linear infinite;
}

@keyframes rf-spin {
  to { transform: rotate(360deg); }
}

.rf-alert {
  margin: 0 0 0.75rem;
  border-radius: 10px;
}

/* Filter lives in the table's own header (BaseTable #toolbar) rather than
   a separate Filters card — one control doesn't earn its own section. */
.rf-table-tools {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.rf-status-filter {
  width: 190px;
}

/* 32px, not the 36px filter-row spec: this is a lone control inside a card
   header, so there is no filter row to keep a shared baseline with, and the
   shorter trigger keeps the header from growing taller than the title. */
.rf-status-filter >>> .app-dropdown__trigger {
  height: 32px;
}

.rf-totals__chip {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  color: #475569;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
}

.rf-name {
  font-weight: 600;
  color: #1f2a44;
}

.rf-name__sub {
  font-size: 0.72rem;
  color: #6b7280;
  font-variant-numeric: tabular-nums;
}

.rf-amount {
  font-weight: 700;
  color: #1f2a44;
  font-variant-numeric: tabular-nums;
}

.rf-reason {
  display: inline-block;
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.badge {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  white-space: nowrap;
}

.badge--green { background: #dff5ea; color: #0e5b46; }
.badge--yellow { background: #fef3c7; color: #92400e; }
.badge--red { background: #ffe4e6; color: #9f1239; }
.badge--gray { background: #eef1f7; color: #4b5563; }

.rf-view-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  height: 28px;
  padding: 0 0.7rem;
  border-radius: 8px;
  font-size: 0.78rem;
  font-weight: 600;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}

.rf-view-btn:hover {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.12);
}

.rf-view-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.rf-pagination-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 0.85rem;
}

.rf-pagination-info {
  font-size: 0.78rem;
  font-weight: 600;
  color: #475569;
}

/* View-refund modal — mirrors the order-detail modal pattern */
.view-order-modal >>> .modal-content {
  border-radius: 14px;
  border: 1px solid #e5e9f2;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.22);
}

.view-order-modal >>> .modal-body {
  padding: 1.1rem;
  background: #fbfcfe;
  max-height: calc(100vh - 220px);
  overflow-y: auto;
}

.view-order-modal >>> .modal-footer .btn {
  border-radius: 8px;
  font-size: 0.85rem;
  min-width: 90px;
  padding: 0.45rem 1.1rem;
}

.vo-body {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.vo-section {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 10px;
  overflow: hidden;
}

.vo-row {
  display: grid;
  grid-template-columns: 200px 1fr;
  align-items: center;
  gap: 0.5rem;
  padding: 0.7rem 0.95rem;
  border-bottom: 1px solid #eef1f7;
}

.vo-row:nth-child(odd) {
  background: #fafbfd;
}

.vo-row:last-child {
  border-bottom: 0;
}

.vo-key {
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
}

.vo-val {
  font-size: 0.88rem;
  color: #1f2a44;
}

.rf-not-actioned {
  color: #6b7280;
  font-style: italic;
}

.rf-modal-amount {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #0e5b46;
}

.vo-section--proof {
  background: #ffffff;
  padding: 0;
}

.rf-proof-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.6rem 0.95rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
}

.rf-proof-title {
  font-size: 0.82rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.rf-proof-open {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.32rem 0.7rem;
  border-radius: 6px;
  font-size: 0.76rem;
  font-weight: 600;
  color: #0e5b46;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  text-decoration: none;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.rf-proof-open:hover {
  border-color: #16a07a;
  background: #f5fbf8;
  color: #0e5b46;
  text-decoration: none;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.12);
}

.rf-proof-preview {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.85rem;
  background: #f8fafc;
  min-height: 220px;
}

.rf-proof-image {
  display: block;
  max-width: 100%;
  max-height: 520px;
  border-radius: 8px;
  border: 1px solid #e5e9f2;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
}

.rf-proof-pdf {
  display: block;
  width: 100%;
  height: 520px;
  border: 1px solid #e5e9f2;
  border-radius: 8px;
  background: #ffffff;
}

.rf-proof-fallback {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  padding: 1.5rem;
  font-size: 0.82rem;
  color: #4b5563;
  text-align: center;
}

.rf-proof-fallback a {
  color: #0e5b46;
  font-weight: 600;
  text-decoration: underline;
}
</style>
